import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  input,
  InputSignal,
  InputSignalWithTransform,
  OnInit,
  OnDestroy,
  Signal,
  viewChild
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatChipListbox, MatChipListboxChange, MatChipOption } from '@angular/material/chips';
import { TranslateDirective } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IdService } from '../../services/id.service';
import { NewValueAccessor } from '../value-accessor/new-value-accessor';

export interface MatFormChipOption {
  value: string;
  text: string;
}

@Component({
  selector: 'td-wb-material-form-chips',
  templateUrl: 'material-form-chips.component.html',
  imports: [FormsModule, ReactiveFormsModule, MatChipListbox, MatChipOption, TranslateDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MaterialFormChipsComponent extends NewValueAccessor<string | Array<string>> implements OnInit, OnDestroy {
  private static readonly ID_PREFIX: string = 'td-wb-material-form-chips';

  private readonly _disabled: Signal<boolean> = computed(() => this._disabledByCva());
  private readonly destroy: Subject<void> = new Subject<void>();

  readonly chipListbox: Signal<MatChipListbox> = viewChild.required<MatChipListbox>('chipListbox');
  readonly _id: InputSignal<string> = input<string>(undefined, { alias: 'id' });
  readonly ariaLabel: InputSignal<string> = input<string>(undefined, { alias: 'aria-label' });
  readonly ariaLabelledBy: InputSignal<string> = input<string>(undefined, { alias: 'aria-labelledby' });
  readonly options: InputSignal<Array<MatFormChipOption>> = input<Array<MatFormChipOption>>([]);

  readonly multiple: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'multiple',
    transform: booleanAttribute
  });

  readonly hideSingleSelectionIndicator: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'hideSingleSelectionIndicator',
    transform: booleanAttribute
  });

  chipsFormControl: FormControl<string | Array<string>>;

  get disabled(): boolean {
    return this._disabled();
  }

  @HostBinding('attr.id') id: string;

  constructor(private readonly idService: IdService) {
    super();
  }

  ngOnInit(): void {
    this.id = this._id() ?? this.idService.generateNamespaceSequentialId(MaterialFormChipsComponent.ID_PREFIX);
    this.chipsFormControl = this.ngControl?.control as FormControl<string | Array<string>> ?? new FormControl<string | Array<string>>(undefined);

    this.chipsFormControl?.valueChanges?.pipe(takeUntil(this.destroy)).subscribe((): void => {
      this.writeValue(this.chipsFormControl.value ?? null);
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  writeValue(value: string | Array<string>): void {
    if (value !== this._value()) {
      this._value.set(value);
    }
    if (this.chipListbox() !== undefined && value !== this.chipListbox()?.value) {
      this.chipListbox().value = value;
    }
  }

  onListboxValueChange(change: MatChipListboxChange): void {
    this.chipsFormControl.setValue(change?.value, { emitEvent: true });
  }
}
